import * as React from 'react'
import { Link } from 'react-router'
import { Check, ChevronRight, FileText, X } from 'lucide-react'
import type {
  ProposalDetailDto,
  ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import { formatDate } from '@lib/helpers/format'
import type { ProposalVersionDecision } from '@lib/input/proposal.input'
import { useDecideProposalVersion } from '@/hooks/useProposalMutations'
import { errorMessage } from '@/helpers/logger'
import { ProposalStatusBadge } from '@/components/ClientBadges'
import { ProposalActivityPanel } from '@/components/ProposalActivityPanel'
import { ProposalDeliveryList } from '@/components/ProposalDeliveryList'
import { ProposalHcpJobs } from '@/components/ProposalHcpJobs'
import { ProposalMeetingRequestList } from '@/components/ProposalMeetingRequestList'
import { isUsablePhoto } from '@/components/ProposalPhotos'
import { ProposalSelectionSummary } from '@/components/ProposalSelectionSummary'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * What went out, in one line: the sites, the photos and the expiry.
 *
 * The content of a sent version cannot change, and all of it is in the
 * document one click away, so it is summed up rather than laid out again as
 * fields that look editable and are not.
 */
function contentLine(version: ProposalVersionDto): string {
  const parts: string[] = []
  parts.push(
    version.sites.length === 0
      ? 'No sites'
      : `Covered ${version.sites.map((site) => site.name).join(', ')}`,
  )
  const photos = version.documents.filter(isUsablePhoto).length
  if (photos > 0) {
    parts.push(`${String(photos)} ${photos === 1 ? 'photo' : 'photos'}`)
  }
  if (version.validUntil !== null) {
    parts.push(`price held until ${formatDate(version.validUntil)}`)
  }
  return parts.join(' · ')
}

interface ProposalSentVersionProps {
  proposal: ProposalDetailDto
  version: ProposalVersionDto
  /** True when any version of this proposal has already been accepted. */
  hasAcceptedVersion: boolean
  /**
   * Earlier versions start closed. The one the client is looking at now is
   * the one the follow-up call is about, and stays open.
   */
  collapsible: boolean
}

/**
 * A version that has gone out: what happened to it, for the follow-up.
 *
 * Read-only by nature. The order is the order of the questions somebody
 * calling the client asks: did they answer, did it arrive, did they ask to
 * meet, and what did they look at.
 */
export function ProposalSentVersion({
  proposal,
  version,
  hasAcceptedVersion,
  collapsible,
}: ProposalSentVersionProps) {
  const decideVersion = useDecideProposalVersion()
  const [open, setOpen] = React.useState<boolean>(!collapsible)
  const [recording, setRecording] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)

  // Driven off the derived status rather than re-deriving from timestamps: the
  // API is the authority on what "still awaiting an answer" means. SENT and
  // EXPIRED are the two states a client can still answer.
  const canDecide =
    (version.status === 'SENT' || version.status === 'EXPIRED') &&
    !hasAcceptedVersion

  async function handleDecision(
    decision: ProposalVersionDecision,
  ): Promise<void> {
    setError(null)
    try {
      await decideVersion.mutateAsync({
        versionId: version.id,
        proposalId: proposal.id,
        input: { decision },
      })
      setRecording(false)
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  const when = version.sentAt
    ? `Sent ${formatDate(version.sentAt)}`
    : `Created ${formatDate(version.createdAt)}`

  return (
    <section className="rounded-lg border border-default bg-surface">
      <header
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 px-4 py-2.5',
          open ? 'border-b border-default' : null,
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          {collapsible ? (
            <h3 className="font-semibold">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setOpen((current) => !current)
                }}
                className="flex items-center gap-1.5"
              >
                <ChevronRight
                  className={cn(
                    'h-3.5 w-3.5 text-muted-text transition-transform',
                    open ? 'rotate-90' : null,
                  )}
                />
                Version {version.versionNumber}
              </button>
            </h3>
          ) : (
            <h2 className="font-semibold">Version {version.versionNumber}</h2>
          )}
          <ProposalStatusBadge status={version.status} />
          <span className="text-xs text-muted-text">
            {when}
            {version.createdByName ? ` by ${version.createdByName}` : ''}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button asChild size="sm" variant="outline">
            <Link
              to={`/proposals/${proposal.id}/versions/${version.id}/document`}
            >
              <FileText className="h-3.5 w-3.5" />
              View
            </Link>
          </Button>
          {canDecide ? (
            <DecisionControls
              recording={recording}
              isPending={decideVersion.isPending}
              onStart={() => {
                setRecording(true)
              }}
              onCancel={() => {
                setRecording(false)
              }}
              onDecide={(decision) => {
                void handleDecision(decision)
              }}
            />
          ) : null}
        </div>
      </header>

      {open ? (
        <div className="space-y-4 p-4">
          {error === null ? null : (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <p className="text-sm text-secondary-text">{contentLine(version)}</p>
          {version.selection === null ? null : (
            <ProposalSelectionSummary selection={version.selection} />
          )}
          {version.status === 'ACCEPTED' ? (
            <ProposalHcpJobs versionId={version.id} />
          ) : null}
          <ProposalDeliveryList
            deliveries={version.deliveries}
            isSent={version.sentAt !== null}
          />
          <ProposalMeetingRequestList
            versionId={version.id}
            isSent={version.sentAt !== null}
          />
          <ProposalActivityPanel
            versionId={version.id}
            hasLink={version.deliveries.length > 0}
          />
        </div>
      ) : null}
    </section>
  )
}

/**
 * Recording an answer the client gave somewhere other than the link.
 *
 * Behind one button rather than two always showing. Side by side in the
 * header they read as the version's status rather than as things to do, and
 * most answers arrive through the link and are recorded without them.
 */
function DecisionControls({
  recording,
  isPending,
  onStart,
  onCancel,
  onDecide,
}: {
  recording: boolean
  isPending: boolean
  onStart: () => void
  onCancel: () => void
  onDecide: (decision: ProposalVersionDecision) => void
}) {
  if (!recording) {
    return (
      <Button size="sm" variant="ghost" onClick={onStart}>
        Record answer
      </Button>
    )
  }
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-xs text-muted-text">The client</span>
      <Button
        size="sm"
        variant="outline"
        disabled={isPending}
        onClick={() => {
          onDecide('ACCEPTED')
        }}
      >
        <Check className="h-3.5 w-3.5" />
        Accepted
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={isPending}
        onClick={() => {
          onDecide('DECLINED')
        }}
      >
        <X className="h-3.5 w-3.5" />
        Declined
      </Button>
      <Button size="sm" variant="ghost" disabled={isPending} onClick={onCancel}>
        Cancel
      </Button>
    </span>
  )
}
